import { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ContractRegisterView,
  ErrorText,
  HelpTip,
} from '@dataroom/shared';
import { useContractRegister, useCorrectContract, useRescanContracts } from '@/api/hooks';
import { useRoomContext } from './context';

/**
 * The room's CONTRACT REGISTER (ADR-0054): every contract filed in the room —
 * leases, customer and supplier agreements, insurance policies, licenses and
 * permits, loans, employment agreements — sorted by expiration, with the dates
 * each document states and plain date flags. The admin can correct any date
 * (audited) and re-read every contract. Read-only once the room is archived.
 */
export function RoomContractsPage() {
  const { roomId, room } = useRoomContext();
  const readOnly = room.lifecycleState !== 'active';
  const register = useContractRegister(roomId);
  const correct = useCorrectContract(roomId);
  const rescan = useRescanContracts(roomId);
  const [rescanned, setRescanned] = useState<number | null>(null);

  return (
    <div className="max-w-6xl space-y-6">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle>Contracts</CardTitle>
            <p className="text-sm text-muted-foreground">
              Key dates of the contracts filed in this room, earliest expiration first.
            </p>
          </div>
          {readOnly ? null : (
            <div className="flex flex-col items-end gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={rescan.isPending}
                onClick={() =>
                  rescan.mutate(undefined, { onSuccess: (r) => setRescanned(r.queued) })
                }
              >
                {rescan.isPending ? 'Starting…' : 'Re-read contracts'}
              </Button>
              {rescanned !== null ? (
                <p role="status" className="text-xs text-muted-foreground">
                  {rescanned === 1
                    ? 'Reading 1 contract again.'
                    : `Reading ${rescanned} contracts again.`}{' '}
                  Corrections you made are kept.
                </p>
              ) : null}
              <ErrorText error={rescan.error} className="text-xs" />
            </div>
          )}
        </CardHeader>
        <CardContent>
          {register.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : register.error ? (
            <ErrorText error={register.error} />
          ) : register.data ? (
            <ContractRegisterView
              register={register.data}
              introAside={<HelpTip id="admin.contracts.flag" />}
              onCorrect={
                readOnly
                  ? undefined
                  : async (documentId, body) => {
                      await correct.mutateAsync({ documentId, body });
                    }
              }
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
